import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { SvgNmrOverlay } from 'cheminfo-font';
import { useCallback, useState, useRef, memo } from 'react';
import { FaChartBar, FaFileExport } from 'react-icons/fa';
import { IoPulseOutline } from 'react-icons/io5';
import { useToggle } from 'react-use';
import { getDataAsString, generateAnalyzeSpectra, } from '../../../data/data1d/multipleSpectraAnalysis';
import { ClipboardFallbackModal } from '../../../utils/clipboard/clipboardComponents';
import { useClipboard } from '../../../utils/clipboard/clipboardHooks';
import { useChartData } from '../../context/ChartContext';
import { useDispatch } from '../../context/DispatchContext';
import { useToaster } from '../../context/ToasterContext';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import { booleanToString } from '../../utility/booleanToString';
import { getSpectraByNucleus } from '../../utility/getSpectraByNucleus';
import { tablePanelStyle } from '../extra/BasicPanelStyle';
import DefaultPanelHeader from '../header/DefaultPanelHeader';
import PreferencesHeader from '../header/PreferencesHeader';
import AlignSpectra from './AlignSpectra';
import AnalysisChart from './AnalysisChart';
import MultipleSpectraAnalysisTable from './MultipleSpectraAnalysisTable';
import MultipleSpectraAnalysisPreferences from './preferences';
function MultipleSpectraAnalysisPanelInner({ activeTab, spectra, showLegend, displayerMode, }) {
    const [isFlipped, setFlipStatus] = useState(false);
    const [calibration, setCalibration] = useState(false);
    const spectraPreferences = usePanelPreferences('spectra', activeTab);
    const preferences = usePanelPreferences('multipleSpectraAnalysis', activeTab);
    const [showAnalysisChart, toggleAnalysisChart] = useToggle(false);
    const spectraAnalysis = generateAnalyzeSpectra(preferences, spectra, activeTab);
    const settingRef = useRef();
    const toaster = useToaster();
    const dispatch = useDispatch();
    const settingsPanelHandler = useCallback(() => {
        setFlipStatus(!isFlipped);
    }, [isFlipped]);
    const saveSettingHandler = useCallback(() => {
        settingRef.current.saveSetting();
    }, []);
    const afterSaveHandler = useCallback(() => {
        setFlipStatus(false);
    }, []);
    const showTrackerHandler = useCallback(() => {
        dispatch({ type: 'TOGGLE_SPECTRA_LEGEND' });
    }, [dispatch]);
    const { rawWriteWithType, cleanShouldFallback, shouldFallback, text } = useClipboard();
    const copyToClipboardHandler = useCallback(() => {
        const data = getDataAsString(spectraAnalysis, spectra, spectraPreferences);
        if (!data)
            return;
        void rawWriteWithType(data).then(() => toaster.show({ message: 'Data copied to clipboard', intent: 'success' }));
    }, [rawWriteWithType, spectra, spectraAnalysis, spectraPreferences, toaster]);
    function handleSpectraCalibration() {
        setCalibration((value) => {
            if (value)
                dispatch({ type: 'RESET_SELECTED_TOOL' });
            return !value;
        });
    }
    let lefButtons = [
        {
            icon: _jsx(FaFileExport, {}),
            tooltip: 'Copy to clipboard',
            onClick: copyToClipboardHandler,
        },
    ];
    if (displayerMode === '1D') {
        lefButtons = lefButtons.concat([
            {
                icon: _jsx(SvgNmrOverlay, {}),
                tooltip: 'Spectra calibration',
                onClick: handleSpectraCalibration,
                active: calibration,
            },
            {
                icon: _jsx(IoPulseOutline, {}),
                tooltip: 'Y spectra tracker',
                onClick: showTrackerHandler,
                active: showLegend,
            },
            {
                icon: _jsx(FaChartBar, {}),
                tooltip: `${booleanToString(!showAnalysisChart)} analysis chart`,
                onClick: toggleAnalysisChart,
                active: showAnalysisChart,
            },
        ]);
    }
    return (_jsxs("div", { css: [
            tablePanelStyle,
            isFlipped &&
                css `
            .table-container th {
              position: relative;
            }
          `,
        ], children: [!isFlipped && (_jsx(DefaultPanelHeader, { deleteToolTip: "Delete All Peaks", onSettingClick: settingsPanelHandler, leftButtons: lefButtons })), isFlipped && (_jsx(PreferencesHeader, { onSave: saveSettingHandler, onClose: settingsPanelHandler })), _jsx("div", { className: "inner-container", children: isFlipped ? (_jsx(MultipleSpectraAnalysisPreferences, { data: spectraAnalysis, activeTab: activeTab, onAfterSave: afterSaveHandler, ref: settingRef })) : calibration ? (_jsx(AlignSpectra, { nucleus: activeTab, onClose: () => setCalibration(false) })) : (_jsxs("div", { style: { overflow: 'auto', maxHeight: '100%' }, children: [showAnalysisChart && (_jsx(AnalysisChart, { spectraAnalysisData: spectraAnalysis })), _jsx(MultipleSpectraAnalysisTable, { data: spectraAnalysis, resortSpectra: preferences.analysisOptions.resortSpectra, activeTab: activeTab })] })) }), _jsx(ClipboardFallbackModal, { mode: shouldFallback, onDismiss: cleanShouldFallback, text: text, label: "Spectra Analysis" })] }));
}
const MemoizedMultipleSpectraAnalysisPanel = memo(MultipleSpectraAnalysisPanelInner);
export default function MultipleSpectraAnalysisPanel() {
    const { data, view: { spectra: { activeTab, showLegend }, }, displayerKey, displayerMode, } = useChartData();
    const spectra = getSpectraByNucleus(activeTab, data);
    if (!activeTab) {
        return _jsx("div", {});
    }
    return (_jsx(MemoizedMultipleSpectraAnalysisPanel, { activeTab, displayerMode, displayerKey, spectra, showLegend }));
}
